import { useCallback, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { Typography } from '@healingpaper/cell'
import { IcArrowDown } from '@healingpaper/cell-icons'

import { SearchPaginationSort } from '@/features/search/api/search.types'

interface SortingDropdownProps {
  currentSort: SearchPaginationSort
  onSortChange: (sort: SearchPaginationSort) => void
}

const SORT_OPTIONS: { value: SearchPaginationSort; labelKey: string }[] = [
  { value: 'RECOMMENDATION', labelKey: 'sorting.recommendation' },
  { value: 'LOWEST_DISCOUNTED_COST', labelKey: 'sorting.lowestPrice' },
  { value: 'HIGHEST_DISCOUNTED_COST', labelKey: 'sorting.highestPrice' },
  { value: 'HIGHEST_POPULARITY', labelKey: 'sorting.popularity' },
  { value: 'HIGHEST_RATING_AMOUNT', labelKey: 'sorting.highestRating' },
  { value: 'HIGHEST_RATING_COUNT', labelKey: 'sorting.mostReviews' },
  { value: 'HIGHEST_DISCOUNT_RATE', labelKey: 'sorting.highestDiscount' },
  { value: 'LATEST_STARTS_AT', labelKey: 'sorting.newest' },
  { value: 'EARLIEST_ENDS_AT', labelKey: 'sorting.endingSoon' },
]

const SortingDropdown = ({ currentSort, onSortChange }: SortingDropdownProps) => {
  const { t } = useTranslation()
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)

  const handleSortSelect = useCallback(
    (sort: SearchPaginationSort, e?: React.MouseEvent) => {
      e?.preventDefault()
      onSortChange(sort)
      setIsOpen(false)
    },
    [onSortChange],
  )

  const currentOption = SORT_OPTIONS.find((option) => option.value === currentSort)
  const isNotDefaultSort = currentSort !== 'RECOMMENDATION'

  // Generate URL for each sorting option
  const getSortUrl = useCallback(
    (sort: SearchPaginationSort) => {
      const query = { ...router.query }
      if (sort === 'RECOMMENDATION') {
        delete query.s
      } else {
        query.s = sort
      }
      return {
        pathname: router.pathname,
        query,
      }
    },
    [router],
  )

  // Generate URLs for all options
  const sortUrls = useMemo(() => {
    return SORT_OPTIONS.map((option) => ({
      ...option,
      url: getSortUrl(option.value),
    }))
  }, [getSortUrl])

  return (
    <div className="relative">
      {/* Main dropdown toggle - still a button for accessibility */}
      <button
        type="button"
        className={`flex flex-row items-center gap-1 px-3 py-2 border-[1px] border-[#D6D6D6] rounded-[8px] ${
          isNotDefaultSort ? 'bg-[#FFF7E0] border-[#FF540F]' : ''
        }`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={t('sorting.label') || 'Sort options'}
      >
        <Typography
          type="caption1"
          elementType="span"
          style={{
            color: isNotDefaultSort ? '#FF540F' : '#666666',
          }}
        >
          {t(currentOption?.labelKey || 'sorting.recommendation')}
        </Typography>
        <IcArrowDown
          width={16}
          height={16}
          color={isNotDefaultSort ? '#FF540F' : '#666666'}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out',
          }}
        />
      </button>

      {/* Visible dropdown menu */}
      {isOpen && (
        <div className="absolute z-10 w-max min-w-full mt-1 bg-white border border-[#D6D6D6] rounded-[8px] shadow-lg">
          <div className="py-1">
            {sortUrls.map((option) => (
              <Link
                key={option.value}
                href={option.url}
                className={`block w-full px-3 py-2 text-left hover:bg-gray-50 focus:outline-none ${
                  currentSort === option.value ? 'bg-[#FFF7E0]' : ''
                }`}
                onClick={(e) => handleSortSelect(option.value, e)}
              >
                <Typography
                  type="caption1"
                  elementType="span"
                  style={{
                    color: currentSort === option.value ? '#FF540F' : '#666666',
                  }}
                >
                  {t(option.labelKey)}
                </Typography>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Hidden links for SEO crawlers - always rendered but visually hidden */}
      <nav className="sr-only" aria-label="Sorting options for search engines">
        {sortUrls.map((option) => (
          <Link key={`seo-${option.value}`} href={option.url} aria-label={`Sort by ${t(option.labelKey)}`}>
            {t(option.labelKey)}
          </Link>
        ))}
      </nav>

      {/* Click outside to close dropdown */}
      {isOpen && <div className="fixed inset-0 z-0" onClick={() => setIsOpen(false)} aria-hidden="true" />}
    </div>
  )
}

export default SortingDropdown
